/**
 * MigrationProgressModal
 *
 * Hand-rolled overlay modal (NOT Chakra's <Modal>). We tried Chakra's
 * Modal first but in this app's tree it leaked into the document flow
 * instead of portaling/overlaying — likely a portal/theme interaction
 * we don't fully control. A plain ``position: fixed`` overlay + content
 * box is reliable, has no Portal dependency, and lets us animate the
 * "in progress" spinner with a global CSS keyframe injected once on
 * first mount.
 *
 * Per-row visual language:
 *   pending     → ⏳ Clock        + gray  "Created"
 *   in_progress → 🔄 Loader2 spin + blue  "In progress"
 *   complete    → ✅ CheckCircle2 + green "Complete"
 *   failed      → ❌ XCircle      + red   "Failed"
 *
 * Closing is allowed; the floating ``MigrationProgressPill`` reopens it.
 */

import React, { ComponentType, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Progress,
  VStack,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import {
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  Users,
  Building2,
  Calendar,
  ListOrdered,
  Bell,
  Plug,
  Circle,
  X,
  LucideProps,
} from 'lucide-react';

import {
  useMigrationProgress,
  MigrationTypeStatus,
} from '@/providers/migration-progress-provider';

type IconCmp = ComponentType<LucideProps>;

const TYPE_LABELS: Record<string, string> = {
  users: 'Users',
  teams: 'Teams',
  schedules: 'Schedules',
  escalation_policies: 'Escalation Policies',
  notification_rules: 'Notification Rules',
  // Scan-only — scan_account covers 6 types, migration only covers 5.
  integrations: 'Integrations',
};

const TYPE_ICONS: Record<string, IconCmp> = {
  users: Users,
  teams: Building2,
  schedules: Calendar,
  escalation_policies: ListOrdered,
  notification_rules: Bell,
  integrations: Plug,
};

interface StatusVisual {
  Icon: IconCmp;
  label: string;
  badgeScheme: string;
  iconColor: string;
  spin: boolean;
  rowBg: string;
  rowBorder: string;
}

const STATUS_VISUALS: Record<MigrationTypeStatus, StatusVisual> = {
  pending: {
    Icon: Clock,
    label: 'Created',
    badgeScheme: 'gray',
    iconColor: '#A0AEC0',
    spin: false,
    rowBg: 'white',
    rowBorder: '#E2E8F0',
  },
  in_progress: {
    Icon: Loader2,
    label: 'In progress',
    badgeScheme: 'blue',
    iconColor: '#0052cc',
    spin: true,
    rowBg: '#EBF4FF',
    rowBorder: '#90CDF4',
  },
  complete: {
    Icon: CheckCircle2,
    label: 'Complete',
    badgeScheme: 'green',
    iconColor: '#06AC38',
    spin: false,
    rowBg: '#F0FFF4',
    rowBorder: '#9AE6B4',
  },
  failed: {
    Icon: XCircle,
    label: 'Failed',
    badgeScheme: 'red',
    iconColor: '#E53E3E',
    spin: false,
    rowBg: '#FFF5F5',
    rowBorder: '#FEB2B2',
  },
};

function getTypeIcon(rt: string): IconCmp {
  return TYPE_ICONS[rt] || Circle;
}
function getTypeLabel(rt: string): string {
  return (
    TYPE_LABELS[rt] ||
    rt.charAt(0).toUpperCase() + rt.slice(1).replace(/_/g, ' ')
  );
}

/** Inject the @keyframes once on first mount; idempotent across re-mounts. */
function useSpinKeyframes() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('__migration_spin_kf__')) return;
    const style = document.createElement('style');
    style.id = '__migration_spin_kf__';
    style.textContent =
      '@keyframes __mig_spin__{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }, []);
}

export const MigrationProgressModal: React.FC = () => {
  const {
    migrationTypes,
    isModalOpen,
    closeModal,
    typesProgressPercent,
    isMigrationActive,
    scanTypes,
    isScanActive,
    scanProgressPercent,
    activePhase,
  } = useMigrationProgress();
  useSpinKeyframes();

  if (!isModalOpen) return null;

  // Pick the stream that drives this render. ``activePhase`` is the
  // primary signal (set by the provider while one phase has live rows).
  // When neither phase is active but the modal is open (user opened it
  // manually post-completion or pre-start) we still need something to
  // render: prefer migration if it has rows, otherwise scan, otherwise
  // an empty migration shape.
  const phase: 'scan' | 'migration' =
    activePhase ??
    (migrationTypes.length > 0
      ? 'migration'
      : scanTypes.length > 0
        ? 'scan'
        : 'migration');
  const rows = phase === 'scan' ? scanTypes : migrationTypes;
  const progressPercent =
    phase === 'scan' ? scanProgressPercent : typesProgressPercent;
  const phaseActive = phase === 'scan' ? isScanActive : isMigrationActive;

  const allFinished =
    rows.length > 0 &&
    rows.every((t) => t.status === 'complete' || t.status === 'failed');

  const title =
    phase === 'scan'
      ? allFinished
        ? 'Scan complete'
        : phaseActive
          ? 'Scanning OpsGenie account'
          : 'Scan progress'
      : allFinished
        ? 'Migration complete'
        : phaseActive
          ? 'Migration in progress'
          : 'Migration progress';

  // Only the scan modal shows the "this may take a few minutes" hint —
  // migration progress is generally fast enough that the hint would be
  // misleading.
  const subtitle =
    phase === 'scan' && phaseActive && !allFinished
      ? 'This process can take a few minutes. Feel free to keep this window open.'
      : null;

  const completedCount = rows.filter(
    (t) => t.status === 'complete' || t.status === 'failed',
  ).length;

  return (
    // Fullscreen overlay (backdrop). Clicking the backdrop closes the modal.
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.600"
      zIndex={2000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={closeModal}
    >
      {/* Content box. Stops click propagation so clicking the box itself
          doesn't close the modal. */}
      <Box
        onClick={(e) => e.stopPropagation()}
        bg="white"
        borderRadius="lg"
        boxShadow="2xl"
        w={{ base: '92%', md: '560px' }}
        maxH="85vh"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <Box px={6} pt={5} pb={4} borderBottomWidth="1px" borderColor="gray.100">
          <Flex justify="space-between" align="flex-start" mb={subtitle ? 1 : 3}>
            <Text fontSize="lg" fontWeight="bold" color="gray.800">
              {title}
            </Text>
            <IconButton
              aria-label="Close"
              icon={<X size={18} />}
              size="sm"
              variant="ghost"
              onClick={closeModal}
            />
          </Flex>
          {subtitle && (
            <Text fontSize="xs" color="gray.500" mb={3}>
              {subtitle}
            </Text>
          )}
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontSize="xs" color="gray.500">
              {progressPercent}% complete
            </Text>
            <Text fontSize="xs" color="gray.500">
              {completedCount}/{rows.length || 0}
            </Text>
          </Flex>
          <Progress
            value={progressPercent}
            size="sm"
            colorScheme={allFinished ? 'green' : 'blue'}
            borderRadius="full"
          />
        </Box>

        {/* Body */}
        <Box px={6} py={5} overflowY="auto">
          {rows.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              {phase === 'scan'
                ? 'Waiting for the scan to start…'
                : 'Waiting for the migration to start…'}
            </Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {rows.map((row) => {
                const visual = STATUS_VISUALS[row.status];
                const TypeIcon = getTypeIcon(row.resource_type);
                const StatusIcon = visual.Icon;
                return (
                  <Flex
                    key={row.resource_type}
                    align="center"
                    justify="space-between"
                    p={3}
                    borderRadius="md"
                    borderWidth="1px"
                    style={{
                      backgroundColor: visual.rowBg,
                      borderColor: visual.rowBorder,
                    }}
                  >
                    <HStack spacing={3}>
                      <Box flexShrink={0}>
                        <TypeIcon size={20} color={visual.iconColor} />
                      </Box>
                      <Text fontWeight="medium" color="gray.800">
                        {getTypeLabel(row.resource_type)}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Box
                        // Inline style + injected @keyframes — avoids any
                        // emotion ``css`` prop quirks in this app.
                        style={
                          visual.spin
                            ? {
                                animation:
                                  '__mig_spin__ 1s linear infinite',
                                display: 'inline-flex',
                              }
                            : { display: 'inline-flex' }
                        }
                      >
                        <StatusIcon size={16} color={visual.iconColor} />
                      </Box>
                      <Badge colorScheme={visual.badgeScheme} variant="subtle">
                        {visual.label}
                      </Badge>
                    </HStack>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </Box>

        {/* Footer */}
        <Box
          px={6}
          py={4}
          borderTopWidth="1px"
          borderColor="gray.100"
          display="flex"
          justifyContent="flex-end"
        >
          <Button
            onClick={closeModal}
            colorScheme={allFinished ? 'green' : 'blue'}
            size="sm"
          >
            {allFinished ? 'Done' : 'Hide'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MigrationProgressModal;
